import * as React from "react"
import { HexColorInput, HexColorPicker } from "react-colorful"

import updatePad from "@podcastsoundboard/ps-lib/api/pads/update"
import PadImagePicker from "@podcastsoundboard/ps-lib/components/PadImagePicker"

import DeleteIcon from "@mui/icons-material/Delete"
import VolumeDown from "@mui/icons-material/VolumeDown"
import VolumeUp from "@mui/icons-material/VolumeUp"
import LoadingButton from "@mui/lab/LoadingButton"
import { Grid } from "@mui/material"
import Box from "@mui/material/Box"
import Checkbox from "@mui/material/Checkbox"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogTitle from "@mui/material/DialogTitle"
import FormControlLabel from "@mui/material/FormControlLabel"
import IconButton from "@mui/material/IconButton"
import Slider from "@mui/material/Slider"
import Stack from "@mui/material/Stack"
import Tab from "@mui/material/Tab"
import Tabs from "@mui/material/Tabs"
import TextField from "@mui/material/TextField"

import deletePad from "../../../../api/pads/delete"
import AudioStackInterface from "../../../../lib/audioStack/AudioStackInterface"
import { useAppDispatch } from "../../../../redux"
import { addSnackbar } from "../../../../redux/snackbars"
import PadComponent from "../Pad"
import { NumberInputPossibleValues } from "../../../../types/Browser"
import Pad from "../../../../types/Pad"
import SoundFilePicker from "../SoundFilePicker"
import FadeInControls from "./components/FadeInControls"
import FadeOutControls from "./components/FadeOutControls"
import FreesoundDetails from "./components/FreesoundDetails"
import MidiControls from "./components/MidiControls"
import ShortcutPicker from "./components/ShortcutPicker"
import SortingControls from "./components/SortingControls"
import StartEndPicker from "./components/StartEndPicker"
import audioStack from "../../../../lib/audioStack"
import { dummySoundboard } from "../../../AdAssets/SoundboardPreview"

function TabPanel(props: {
  children: React.ReactNode
  value: number
  index: number
}) {
  const { children, value, index } = props

  return (
    <div
      role="tabpanel"
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      <Box
        height={value === index ? "auto" : "0px"}
        style={{ overflow: "hidden" }}
      >
        {children}
      </Box>
    </div>
  )
}

export default function EditPadModal({
  pad,
  audioInterface,
  open,
  handleClose,
  onUpdate,
  disabled,
}: {
  pad: Pad
  audioInterface: AudioStackInterface
  open: boolean
  handleClose: () => void
  onUpdate: () => void
  disabled?: boolean
}) {
  const dispatch = useAppDispatch()

  const [submitting, setSubmitting] = React.useState(false)
  const [tabValue, setTabValue] = React.useState(0)
  const [sortIndex, setSortIndex] = React.useState(0)
  const [deleting, setDeleting] = React.useState(false)
  const [deleteAreYouSureOpen, setDeleteAreYouSureOpen] = React.useState(false)
  const [soundFileUuid, setSoundFileUuid] = React.useState("")

  const [name, setName] = React.useState("")
  const [color, setColor] = React.useState("#bb36d1")
  const [loop, setLoop] = React.useState(false)
  const [multishot, setMultishot] = React.useState(false)
  const [alwaysPlayFromStart, setAlwaysPlayFromStart] = React.useState(false)
  const [volume, setVolume] = React.useState(1)
  const [startTime, setStartTime] =
    React.useState<NumberInputPossibleValues>(null)
  const [endTime, setEndTime] = React.useState<NumberInputPossibleValues>(null)
  const [midiBinding, setMidiBinding] = React.useState("")
  const [shortcut, setShortcut] = React.useState("")
  const [imageFile, setImageFile] = React.useState<File | null>(null)
  const [coverImageUrl, setCoverImageUrl] = React.useState("")

  const [fadeOutDuration, setFadeOutDuration] =
    React.useState<NumberInputPossibleValues>(0)
  const [fadeOutOnPressDuration, setFadeOutOnPressDuration] =
    React.useState<NumberInputPossibleValues>(0)
  const [autoFadeInDuration, setAutoFadeInDuration] =
    React.useState<NumberInputPossibleValues>(0)
  const [fadeInOnPressDuration, setFadeInOnPressDuration] =
    React.useState<NumberInputPossibleValues>(0)

  // form was remembering pad from different soundboard on desktop
  // though this was probably due to pads being keyed by letters instead of uuids
  const [hasReceivedPad, setHasReceivedPad] = React.useState(false)

  React.useEffect(() => {
    if (imageFile) {
      const reader = new FileReader()
      reader.readAsDataURL(imageFile)
      reader.onloadend = () => setCoverImageUrl(reader.result as string)
    }
  }, [imageFile])

  const receivePad = React.useCallback(
    (pad: Pad) => {
      if (!hasReceivedPad) {
        setSoundFileUuid(pad.soundFileUuid)
        setName(pad.name)
        setColor(pad.color || "#bb36d1")
        setLoop(pad.loop)
        setMultishot(pad.multishot)
        setAlwaysPlayFromStart(pad.alwaysPlayFromStart)
        setVolume(pad.volume)
        setStartTime(pad.startTime)
        setEndTime(pad.endTime)
        setMidiBinding(pad.midiBinding)
        setShortcut(pad.shortcut)
        setSortIndex(pad.sortIndex)

        setFadeOutDuration(pad.fadeOutDuration || 0)
        setFadeOutOnPressDuration(pad.fadeOutOnPressDuration || 0)
        setAutoFadeInDuration(pad.autoFadeInDuration || 0)
        setFadeInOnPressDuration(pad.fadeInOnPressDuration || 0)
        setCoverImageUrl(pad.coverImageUrl || "")
      }
    },
    [hasReceivedPad],
  )

  React.useEffect(() => {
    open && receivePad(pad)
  }, [pad, open, receivePad])

  React.useEffect(() => {
    if (!open) setHasReceivedPad(false)
  }, [open])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const newPadParams = {
    soundFileUuid,

    name,
    color,
    loop,
    multishot,
    alwaysPlayFromStart,
    volume,
    startTime,
    endTime,
    midiBinding,
    shortcut,
    sortIndex,

    fadeOutDuration: fadeOutDuration == null ? null : Number(fadeOutDuration),
    fadeOutOnPressDuration:
      fadeOutOnPressDuration == null ? null : Number(fadeOutOnPressDuration),
    autoFadeInDuration:
      autoFadeInDuration == null ? null : Number(autoFadeInDuration),
    fadeInOnPressDuration:
      fadeInOnPressDuration == null ? null : Number(fadeInOnPressDuration),
    coverImageFile: imageFile || undefined,
    clearImage: !imageFile && !coverImageUrl,
  }

  const handleSubmit = React.useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      setSubmitting(true)
      updatePad(pad.uuid, newPadParams)
        .then(() => {
          setSubmitting(false)
          dispatch(addSnackbar({ text: "Updated pad." }))
          onUpdate && onUpdate()
        })
        .catch((err) => {
          console.error(err)
          setSubmitting(false)
          dispatch(addSnackbar({ text: "Failed to update pad." }))
        })
    },
    [dispatch, newPadParams, onUpdate, pad.uuid],
  )

  const deleteAction = () => {
    setDeleting(true)
    audioInterface.pause()
    deletePad(pad.uuid)
      .then(() => {
        setDeleting(false)
        dispatch(addSnackbar({ text: "Deleted pad." }))
        onUpdate && onUpdate()
      })
      .catch((err) => {
        console.error(err)
        setDeleting(false)
        dispatch(addSnackbar({ text: "Failed to delete pad." }))
      })
  }

  const canChooseStartEnd = !pad || pad.soundFileUuid === soundFileUuid

  const freesoundId = pad && pad.soundfile && pad.soundfile.freesoundId

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="md"
    >
      <Box
        display="flex"
        flexDirection="row"
        alignItems="flex-start"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <DialogTitle id="alert-dialog-title">
          Edit Sound {disabled && "(Locked)"}
        </DialogTitle>

        <IconButton
          disabled={disabled}
          onClick={() => setDeleteAreYouSureOpen(true)}
          sx={{ my: 1, mx: 2 }}
          aria-label="back"
        >
          <DeleteIcon style={{ height: 30, width: 30 }} />
        </IconButton>

        <Dialog
          open={deleteAreYouSureOpen}
          onClose={() => setDeleteAreYouSureOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle id="alert-dialog-title">
            Are you sure you want to delete this pad?
          </DialogTitle>
          <DialogActions>
            <LoadingButton
              loading={deleting}
              onClick={deleteAction}
              variant="contained"
            >
              Delete
            </LoadingButton>
            <LoadingButton onClick={() => setDeleteAreYouSureOpen(false)}>
              Cancel
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Box>

      <Box sx={{ pt: 0 }}>
        <Box
          sx={{
            borderBottom: 1,
            borderTop: 1,
            borderColor: "divider",
            mb: 3,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={(_e, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab
              sx={{ px: 3 }}
              role="tab"
              aria-label="General"
              label="General"
            />
            <Tab sx={{ px: 3 }} role="tab" aria-label="Trim" label="Trim" />
            <Tab
              sx={{ px: 3 }}
              role="tab"
              aria-label="Shortcut"
              label="Shortcut"
            />
            <Tab
              sx={{ px: 3 }}
              role="tab"
              aria-label="Fade Out"
              label="Fade Out"
            />
            <Tab
              sx={{ px: 3 }}
              role="tab"
              aria-label="Fade In"
              label="Fade In"
            />
            <Tab sx={{ px: 3 }} role="tab" aria-label="MIDI" label="MIDI" />
            <Tab sx={{ px: 3 }} role="tab" aria-label="Order" label="Order" />
            <Tab sx={{ px: 3 }} role="tab" aria-label="Image" label="Image" />
            {freesoundId && (
              <Tab
                sx={{ px: 3 }}
                role="tab"
                aria-label="License"
                label="License"
              />
            )}
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            sx={{ px: 4, pb: 2 }}
            style={{ width: "100%" }}
          >
            <Grid item xs={12} sm={9}>
              <TextField
                name="name"
                required
                id="name"
                fullWidth
                value={name || ""}
                label="Name"
                onChange={(e) => setName(e.target.value)}
                sx={{ mt: 1 }}
              />

              <Stack
                direction="row"
                flexWrap="wrap"
                justifyContent="space-between"
                style={{ width: "100%" }}
              >
                <Box sx={{ mt: 1, mr: 2, width: "100%" }}>
                  <Box sx={{ pt: 2, width: "100%", pb: 1 }}>
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <VolumeDown />
                      <Slider
                        sx={{ mx: 2 }}
                        style={{ color }}
                        min={0}
                        max={100}
                        name="Volume"
                        value={volume}
                        onChange={(_e, v) => setVolume(Number(v))}
                        valueLabelDisplay="auto"
                      />
                      <VolumeUp />
                    </Box>
                  </Box>
                </Box>
                {/* </Grid> */}

                {/* <Grid
                  item
                  sx={{ mt: 1, mb: 4 }}
                  xs={12}
                  md={4}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                  }}
                > */}
                {/* </Grid> */}
              </Stack>

              <FormControlLabel
                sx={{ pt: 1, pr: 2 }}
                label="Loop?"
                control={
                  <Checkbox
                    checked={loop || false}
                    onChange={(e) => setLoop(e.target.checked)}
                  />
                }
              />
              <FormControlLabel
                sx={{ pt: 1, pr: 2 }}
                label="Multi-fire?"
                control={
                  <Checkbox
                    checked={multishot || false}
                    onChange={(e) => setMultishot(e.target.checked)}
                  />
                }
              />
              <FormControlLabel
                sx={{ pt: 1, pr: 2 }}
                label="Always play from start?"
                control={
                  <Checkbox
                    checked={alwaysPlayFromStart || false}
                    onChange={(e) => setAlwaysPlayFromStart(e.target.checked)}
                  />
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={3}
              className="small"
              display="flex"
              sx={{ mt: 1, mb: 4 }}
              flexDirection="column"
              alignItems="flex-end"
            >
              <HexColorPicker
                color={color}
                onChange={(newColor) => setColor(newColor)}
              />
              <HexColorInput
                color={color}
                onChange={(newColor) => setColor(newColor)}
              />
            </Grid>
          </Grid>

          <Box sx={{ px: 4, pb: 4, pt: 2 }}>
            {!disabled && (
              <Box>
                <SoundFilePicker
                  color={color}
                  setName={(name) => setName(name)}
                  soundFileUuid={soundFileUuid}
                  setSoundFileUuid={setSoundFileUuid}
                />
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 2, pb: 2 }}>
            {tabValue === 1 && (
              <StartEndPicker
                open={open}
                pad={pad}
                disabled={!canChooseStartEnd}
                startTime={startTime || 0}
                endTime={endTime || 0}
                onStartEndChange={(startTime, endTime) => {
                  // this configuration allows us to backspace the start/end times and
                  // show the placeholder values

                  if (startTime === "" || startTime === null) {
                    setStartTime(null)
                  } else {
                    if (!isNaN(Number(startTime)))
                      setStartTime(Number(startTime))
                  }

                  if (endTime === "" || endTime === null) {
                    setEndTime(null)
                  } else {
                    if (!isNaN(Number(endTime))) setEndTime(Number(endTime))
                  }
                }}
              />
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ px: 2, pb: 2 }}>
            <ShortcutPicker shortcut={shortcut} setShortcut={setShortcut} />
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box sx={{ px: 2, pb: 2 }}>
            <FadeOutControls
              color={color}
              autoFadeOutDuration={fadeOutDuration}
              setAutoFadeOutDuration={setFadeOutDuration}
              fadeOutOnPressDuration={fadeOutOnPressDuration}
              setFadeOutOnPressDuration={setFadeOutOnPressDuration}
            />
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Box sx={{ px: 2, pb: 2 }}>
            <FadeInControls
              color={color}
              autoFadeInDuration={autoFadeInDuration}
              setAutoFadeInDuration={setAutoFadeInDuration}
              fadeInOnPressDuration={fadeInOnPressDuration}
              setFadeInOnPressDuration={setFadeInOnPressDuration}
            />
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <Box sx={{ px: 2, pb: 2 }}>
            <MidiControls
              midiBinding={midiBinding}
              setMidiBinding={setMidiBinding}
            />
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          <Box sx={{ px: 2, pb: 2 }}>
            <SortingControls
              sortIndex={sortIndex}
              setSortIndex={setSortIndex}
            />
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={7}>
          <Box sx={{ px: 2, pb: 2 }}>
            <PadImagePicker
              onClear={() => {
                setImageFile(null)
                setCoverImageUrl("")
              }}
              imgSrc={coverImageUrl}
              onImageSelected={async (_name, _data, selectedImage) => {
                try {
                  selectedImage && setImageFile(selectedImage)
                } catch (err) {
                  console.error("Failed to select image", err)
                  dispatch(addSnackbar({ text: "Failed to select image." }))
                }
              }}
              onError={(error) => dispatch(addSnackbar({ text: error }))}
            />
            <Box maxWidth="250px" sx={{ mt: 4 }}>
              <PadComponent
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                overrideOnCogPress={() => {}}
                audioInterface={audioStack.interfaceForSoundboardPad(
                  pad.soundboardUuid,
                  pad.uuid,
                )}
                pad={{
                  ...pad,
                  ...newPadParams,
                  startTime: Number(startTime || 0),
                  endTime: Number(endTime || 0),
                  fadeOutDuration: Number(fadeOutDuration || 0),
                  autoFadeInDuration: Number(autoFadeInDuration || 0),
                  fadeInOnPressDuration: Number(fadeInOnPressDuration || 0),
                  fadeOutOnPressDuration: Number(fadeOutOnPressDuration || 0),
                  coverImageUrl,
                }}
                overrideSoundboard={{
                  ...dummySoundboard,
                  uuid: pad.soundboardUuid,
                }}
                solo={false}
                keyIndex={0}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                setDragDropSaving={() => {}}
              />
            </Box>
          </Box>
        </TabPanel>

        {freesoundId && (
          <TabPanel value={tabValue} index={8}>
            <Box sx={{ px: 2, pb: 2 }}>
              <FreesoundDetails freesoundId={freesoundId} />
            </Box>
          </TabPanel>
        )}
      </Box>

      <DialogActions>
        <LoadingButton
          disabled={disabled}
          loading={submitting}
          onClick={handleSubmit}
          style={disabled ? {} : { backgroundColor: color }}
          variant="contained"
        >
          Save
        </LoadingButton>
        <LoadingButton style={{ color }} onClick={handleClose}>
          Cancel
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
